import { useCallback, useState } from "react";
import { formatTemplate } from "@/i18n/use-sso-locale";

/**
 * Helpers shared by the SSO admin cards (domains, providers, role mapping,
 * break-glass). They live here rather than being copied per card so a refusal
 * code or a timestamp renders the same way wherever it appears.
 */

/** Reason codes that mean "this caller may not do this" rather than a transport
 * or server fault. */
export const DENIED_REASON_CODES = [
  "HTTP_401",
  "HTTP_403",
  "AUTH_INSUFFICIENT_SCOPE",
  "AUTH_INSUFFICIENT_PERMISSION",
  "AUTH_HUMAN_PRINCIPAL_REQUIRED",
];

/** Reason codes that mean the SSO admin API is not answering here at all, so
 * the page must say so instead of rendering an empty list. `ROUTE_NOT_FOUND` is
 * what the gateway returns for a route it has not registered yet (the same code
 * the hot-wallets pages read), and `NETWORK_ERROR` is the transport-failure
 * code the SSO fetchers return. */
export const UNAVAILABLE_REASON_CODES = [
  "ROUTE_NOT_FOUND",
  "HTTP_404",
  "HTTP_502",
  "HTTP_503",
  "HTTP_504",
  "NETWORK_ERROR",
  "AUTH_POLICY_UNAVAILABLE",
];

/** Localized text for a refusal reason code, falling back to a template that
 * keeps the raw code visible rather than swallowing it. */
export function refusalMessage(ui: Record<string, string>, reasonCode: string | undefined): string | null {
  if (!reasonCode) return null;
  return ui[`sso.refusal.${reasonCode}`]
    ?? formatTemplate(ui["sso.refusal.unknown"], { reasonCode });
}

/** Renders a server timestamp in the operator's locale; an absent or unparsable
 * value is shown as an em dash rather than a client-side guess. */
export function formatTimestamp(value: string | null | undefined): string {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
}

/** Clipboard write with a short "Copied" acknowledgement. A denied clipboard is
 * a soft failure: the value is already on screen and selectable. */
export function useClipboard(resetMs = 2000) {
  const [copied, setCopied] = useState<string | null>(null);

  const copy = useCallback(async (key: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(key);
      window.setTimeout(() => setCopied((current) => (current === key ? null : current)), resetMs);
    } catch {
      // See doc comment.
    }
  }, [resetMs]);

  return { copied, copy };
}
